import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Eye, 
  ShieldCheck, 
  FileText, 
  Lock, 
  Database, 
  Server, 
  Cpu, 
  Mail, 
  ExternalLink,
  ChevronRight,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';
import EditorialHero from '@/components/ui/EditorialHero';
import { Content, Language } from '@/types';

interface PrivacyPolicyProps {
  content: Content;
  lang: Language;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ content, lang }) => {
  const isAr = lang === 'ar';
  const isRtl = isAr;
  const data = content.legal.privacy;

  const [activeSectionIdx, setActiveSectionIdx] = useState<number>(0);

  const privacyHighlights = [
    {
      icon: ShieldCheck,
      titleEn: 'Zero-Model Training',
      titleAr: 'انعدام التدريب التام',
      descEn: 'Your coordinates, polygons, and satellite rasters are NEVER used to train or fine-tune any AI models.',
      descAr: 'إحداثياتك ومضلعاتك المكانية وصور الأقمار الصناعية لن تُستخدم مطلقاً في تدريب أو ضبط أي نماذج ذكاء اصطناعي.'
    },
    {
      icon: Cpu,
      titleEn: 'Ephemeral In-Memory',
      titleAr: 'معالجة آنية مؤقتة',
      descEn: 'Uploaded vector files and multi-spectral tensors are processed in RAM enclaves and purged upon session close.',
      descAr: 'تتم معالجة الطبقات المتجهية ومصفوفات الأطياف في الذاكرة الحية فقط ويتم تطهيرها فور انتهاء الجلسة.'
    },
    {
      icon: Lock,
      titleEn: 'Cryptographic Sovereignty',
      titleAr: 'تشفير وسيادة مطلقة',
      descEn: 'Enforced TLS 1.3 in-transit and AES-256 at-rest encryption across all storage and execution boundaries.',
      descAr: 'تشفير إلزامي عبر بروتوكول TLS 1.3 أثناء النقل ومعيار AES-256 في حالة السكون لكافة العمليات.'
    },
    {
      icon: Database,
      titleEn: 'Statutory Data Protection',
      titleAr: 'مطابقة تشريعية قانونية',
      descEn: 'Full compliance with Egyptian Personal Data Protection Law No. 151/2020 and global privacy standards.',
      descAr: 'مطابقة شاملة لأحكام قانون حماية البيانات الشخصية المصري رقم 151 لسنة 2020 والمعايير الدولية.'
    }
  ];

  return (
    <PageTransition>
      <SEO 
        title={isAr ? "وثيقة الخصوصية وسيادة البيانات المكانية | منظومة فكسل" : "Privacy & Data Sovereignty Policy | VEXL Ecosystem"}
        description={
          isAr 
            ? "وثيقة الخصوصية وحماية البيانات المكانية لمنظومة فكسل. ضمانات انعدام التدريب والتشفير وفق القانون المصري رقم 151 لسنة 2020."
            : "Official Privacy Policy and Geospatial Data Sovereignty statement for VEXL Ecosystem (Zero customer training, TLS 1.3 & AES-256)."
        }
        lang={lang}
      />

      <div 
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] relative overflow-hidden selection:bg-[#0E93A3]/30 ${
          isRtl ? 'font-arabic text-right' : 'font-sans text-left'
        }`} 
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <RasterGridBackdrop type="type-a" />

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 pt-20 md:pt-24 pb-24">
          
          {/* Canonical Two-Column Editorial Split Hero */}
          <EditorialHero
            title={data.title}
            description={
              isAr
                ? 'وثيقة السيادة وحماية الخصوصية الرقمية. نلتزم بأعلى معايير الأمان المعتمدة لحماية حدودك الجيومكانية وبياناتك الحساسة دون أي تسريب أو تدريب للنماذج.'
                : 'Our unequivocal commitment to geospatial data sovereignty. We guarantee that your physical boundaries, coordinate databases, and multi-spectral sensors remain exclusively yours.'
            }
            actions={[
              { 
                label: isAr ? 'شروط الخدمة' : 'Terms of Use', 
                href: '/terms', 
                variant: 'secondary' 
              },
              { 
                label: isAr ? 'معمارية الأمان والسيادة' : 'Security Architecture', 
                href: '/security', 
                variant: 'secondary' 
              }
            ]}
            isAr={isAr}
            className="mb-10"
          />

            {/* Core Architectural Guarantees Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
              {privacyHighlights.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-4 rounded-2xl bg-[#12161A]/80 border border-[#262C31] hover:border-[#0E93A3]/40 transition-all backdrop-blur-sm"
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#0E93A3]/10 border border-[#0E93A3]/20 flex items-center justify-center text-[#0E93A3] mb-3">
                      <Icon size={16} />
                    </div>
                    <h3 className="text-sm font-bold text-white mb-1.5">
                      {isAr ? item.titleAr : item.titleEn}
                    </h3>
                    <p className="text-xs text-[#94A3B8] leading-relaxed">
                      {isAr ? item.descAr : item.descEn}
                    </p>
                  </div>
                );
              })}
            </div>

          {/* Main Layout: Sticky Sidebar TOC + Content Blocks */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            
            {/* Desktop Table of Contents Sidebar */}
            <aside className="lg:col-span-4 hidden lg:block sticky top-36 space-y-4">
              <div className="p-5 rounded-2xl bg-[#12161A]/90 border border-[#262C31] backdrop-blur-md">
                <p className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-bold mb-4 flex items-center gap-2">
                  <FileText size={14} />
                  {isAr ? 'فهرس بنود الخصوصية' : 'Privacy Chapters'}
                </p>
                <nav className="space-y-1.5">
                  {data.sections.map((section, idx) => (
                    <a
                      key={idx}
                      href={`#privacy-chapter-${idx + 1}`}
                      onClick={() => setActiveSectionIdx(idx)}
                      className={`block px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                        activeSectionIdx === idx
                          ? 'bg-[#0E93A3]/15 text-[#0E93A3] border-r-2 border-[#0E93A3] font-bold'
                          : 'text-[#94A3B8] hover:text-white hover:bg-white/[0.04]'
                      }`}
                    >
                      {section.heading}
                    </a>
                  ))}
                </nav>

                <div className="mt-6 pt-5 border-t border-[#262C31]">
                  <Link
                    to="/security"
                    className="flex items-center justify-between text-xs text-[#94A3B8] hover:text-[#0E93A3] transition-colors group"
                  >
                    <span className="flex items-center gap-2">
                      <Lock size={14} className="text-[#0E93A3]" />
                      {isAr ? 'معمارية الأمان والنشر المعزول' : 'Security Architecture'}
                    </span>
                    <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Data Protection Officer Card */}
              <div className="p-5 rounded-2xl bg-[#090C10] border border-[#262C31] space-y-3">
                <p className="text-xs font-bold text-white flex items-center gap-2">
                  <Mail size={14} className="text-[#0E93A3]" />
                  {isAr ? 'مكتب حماية البيانات (DPO)' : 'Data Protection Officer Desk'}
                </p>
                <p className="text-xs text-[#94A3B8] leading-relaxed">
                  {isAr
                    ? 'لممارسة حقوق الحذف الفوري أو تصدير الأرشيف الجيومكاني أو توقيع اتفاقيات DPA:'
                    : 'To exercise data erasure rights, export analytical telemetry, or execute enterprise DPAs:'}
                </p>
                <a
                  href="mailto:vexl.gis@gmail.com"
                  className="inline-flex items-center gap-2 text-xs font-mono text-[#0E93A3] hover:underline font-semibold"
                >
                  vexl.gis@gmail.com
                  <ExternalLink size={12} />
                </a>
              </div>
            </aside>

            {/* Articles Stream */}
            <main className="lg:col-span-8 space-y-6">
              {data.sections.map((section, idx) => (
                <section
                  id={`privacy-chapter-${idx + 1}`}
                  key={idx}
                  className="p-6 sm:p-8 rounded-2xl bg-[#12161A]/70 border border-[#262C31] hover:border-[#262C31]/90 transition-all backdrop-blur-sm"
                >
                  <div className="flex items-center justify-between gap-4 mb-4 pb-3 border-b border-[#262C31]/60">
                    <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2.5">
                      <span className="text-[#0E93A3] font-mono text-sm font-semibold">
                        §{idx + 1}.0
                      </span>
                      {section.heading}
                    </h2>
                    <span className="text-[10px] font-mono text-[#64748B] uppercase tracking-widest hidden sm:inline">
                      SOVEREIGN POLICY
                    </span>
                  </div>

                  <div className="space-y-4">
                    {section.content.map((paragraph, pIdx) => (
                      <p 
                        key={pIdx} 
                        className="text-[#94A3B8] text-sm sm:text-base leading-relaxed font-normal"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}

              {/* Security & Terms Navigation Card */}
              <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-r from-[#12161A] to-[#0E93A3]/5 border border-[#262C31] mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <h3 className="text-base font-bold text-white mb-1">
                    {isAr ? 'الامتثال الصارم لقانون حماية البيانات' : 'Strict Regulatory Sovereignty'}
                  </h3>
                  <p className="text-xs text-[#94A3B8]">
                    {isAr 
                      ? 'ضمانات تعاقدية بعدم معالجة البيانات خارج البيئة المصرح بها، مع دعم بيئات العمل المعزولة.'
                      : 'Contractually bounded processing with zero external data egress and sovereign on-premise support.'}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Link
                    to="/terms"
                    className="px-4 py-2 rounded-full bg-[#12161A] border border-[#262C31] hover:border-[#0E93A3] text-xs font-semibold text-white transition-all"
                  >
                    {isAr ? 'شروط الخدمة' : 'Terms of Service'}
                  </Link>
                  <Link
                    to="/security"
                    className="px-4 py-2 rounded-full bg-[#0E93A3] text-black text-xs font-bold hover:bg-[#0bb2c6] transition-all shadow-[0_0_15px_rgba(14,147,163,0.3)]"
                  >
                    {isAr ? 'مركز الأمان' : 'Security Portal'}
                  </Link>
                </div>
              </div>

            </main>

          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default PrivacyPolicy;
